<template>
    <div class="interactive-map" :class="{ 'static-map': isStatic, 'non-clickable': !clickable }">
        <div id="map" ref="mapContainer"></div>
    </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import projectService from '@/services/projectService.js'

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
    name: 'InteractiveMap',
    props: {
        activeFilters: {
            type: Array,
            default: () => []
        },
        focusedProject: {
            type: Object,
            default: null
        },
        isStatic: {
            type: Boolean,
            default: false
        },
        staticZoom: {
            type: Number,
            default: 18
        },
        clickable: {
            type: Boolean,
            default: true
        },
        draggable: {
            type: Boolean,
            default: true
        },
        scrollable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            map: null,
            markers: [],
            projects: []
        }
    },
    async mounted() {
        this.initMap()
        await this.loadProjects()
        this.addMarkers()
        if (this.focusedProject) {
            this.focusOnProject()
        }
    },
    watch: {
        activeFilters: {
            handler() {
                this.$nextTick(() => {
                    this.updateMarkersVisibility()
                })
            },
            deep: true
        },
        focusedProject: {
            handler() {
                if (this.focusedProject) {
                    this.$nextTick(() => {
                        this.focusOnProject()
                    })
                }
            },
            deep: true
        }
    },
    methods: {
        async loadProjects() {
            try {
                this.projects = await projectService.fetchProjects()
            } catch (error) {
                console.error('Failed to load projects:', error)
                this.projects = []
            }
        },
        initMap() {
            // Center coordinates for Bruchköbel area or focused project
            let center = [50.1950, 8.9280]
            let zoom = 13
            
            // If this is a static map with focused project, center on that project
            if (this.isStatic && this.focusedProject) {
                center = [parseFloat(this.focusedProject.lat), parseFloat(this.focusedProject.lng)]
                zoom = this.staticZoom
            }
            
            this.map = L.map(this.$refs.mapContainer, {
                center: center,
                zoom: zoom,
                minZoom: 13,
                maxZoom: 18,
                zoomControl: !this.isStatic && this.scrollable,
                scrollWheelZoom: !this.isStatic && this.scrollable,
                doubleClickZoom: !this.isStatic && this.scrollable,
                touchZoom: !this.isStatic && this.scrollable,
                dragging: !this.isStatic && this.draggable,
                keyboard: !this.isStatic,
                boxZoom: !this.isStatic,
                fadeAnimation: false,
                zoomAnimation: false,
                markerZoomAnimation: false,
                animate: false,
                inertia: false,
                worldCopyJump: false
            })

            // Use neutral grey/white map tiles to match design
            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                attribution: '© OpenStreetMap contributors © CARTO',
                maxZoom: 18,
                minZoom: 13,
                subdomains: 'abcd'
            }).addTo(this.map)
        },
        
        createCustomIcon(status) {
            const colors = {
                'abgeschlossen': '#2fe044', // green
                'geplant': '#0076ff',       // blue  
                'laufend': '#ffba00'        // yellow/orange
            }
            
            const color = colors[status] || '#707070'
            
            return L.divIcon({
                className: 'custom-marker',
                html: `<div class="marker-pin" style="background-color: ${color}; border-radius: 50%; width: 16px; height: 16px; opacity: 0.8;"></div>`,
                iconSize: [16, 16],
                iconAnchor: [8, 8]
            })
        },
        
        addMarkers() {
            if (!this.map) return
            
            // If static mode, only show the focused project
            const projectsToShow = this.isStatic && this.focusedProject 
                ? [this.focusedProject] 
                : this.projects
                
            // Filter projects to only include those with valid coordinates
            const validProjects = projectsToShow.filter(project => {
                const lat = parseFloat(project.lat)
                const lng = parseFloat(project.lng)
                return !isNaN(lat) && !isNaN(lng) && project.lat !== "" && project.lng !== ""
            })
                
            validProjects.forEach(project => {
                try {
                    const coordinates = [parseFloat(project.lat), parseFloat(project.lng)]
                    const marker = L.marker(coordinates, {
                        icon: this.createCustomIcon(project.status)
                    })
                    
                    // Create popup content with marker and project name
                    const popupContent = `
                        <div class="project-popup">
                            <div class="popup-content-wrapper">
                                <div class="popup-marker" style="background-color: ${this.getProjectColor(project.status)};"></div>
                                <h4 class="project-title" data-project-name="${project.name}">${project.name}</h4>
                            </div>
                        </div>
                    `
                    
                                            // Only add popups and interactions if not in static mode and clickable
                        if (!this.isStatic && this.clickable) {
                            marker.bindPopup(popupContent, {
                                maxWidth: 250,
                                className: 'project-popup-container',
                                closeButton: false,
                                offset: [0, -10],
                                direction: 'top',
                                permanent: false,
                                sticky: true
                            })
                            
                            // Add click event to project title
                            marker.on('popupopen', () => {
                                const titleElement = document.querySelector(`[data-project-name="${project.name}"]`)
                                if (titleElement) {
                                    titleElement.addEventListener('click', () => {
                                        this.$emit('project-clicked', project)
                                        marker.closePopup()
                                    })
                                }
                            })
                        }
                    
                    // Store project info in marker for filtering
                    marker.projectData = project
                    
                    this.markers.push(marker)
                    if (this.map && !this.map._container._leaflet_removed) {
                        marker.addTo(this.map)
                    }
                } catch (error) {
                    console.warn('Error adding marker:', error)
                }
            })
        },
        
        updateMarkersVisibility() {
            if (!this.map || this.map._animatingZoom) return
            
            this.markers.forEach(marker => {
                try {
                    const project = marker.projectData
                    let shouldShow = true
                    
                    if (this.activeFilters.length > 0) {
                        const statusFilters = this.activeFilters.filter(f => 
                            ['laufend', 'abgeschlossen', 'geplant'].includes(f)
                        )
                        const locationFilters = this.activeFilters.filter(f => 
                            ['niederissigheim', 'oberissigheim', 'rossdorf', 'butterstadt', 'innenstadt'].includes(f)
                        )
                        
                        let statusMatch = statusFilters.length === 0 || statusFilters.includes(project.status)
                        let locationMatch = locationFilters.length === 0 || locationFilters.includes(project.district?.toLowerCase())
                        
                        shouldShow = statusMatch && locationMatch
                    }
                    
                    if (shouldShow) {
                        if (this.map && this.map.hasLayer && !this.map.hasLayer(marker)) {
                            marker.addTo(this.map)
                        }
                    } else {
                        if (this.map && this.map.hasLayer && this.map.hasLayer(marker)) {
                            this.map.removeLayer(marker)
                        }
                    }
                } catch (error) {
                    console.warn('Error updating marker visibility:', error)
                }
            })
        },
        
        getLocationDisplayName(location) {
            const names = {
                'niederissigheim': 'Niederissigheim',
                'oberissigheim': 'Oberissigheim', 
                'rossdorf': 'Roßdorf',
                'butterstadt': 'Butterstadt',
                'innenstadt': 'Innenstadt'
            }
            return names[location] || location
        },
        
        getStatusDisplayName(status) {
            const names = {
                'abgeschlossen': 'Abgeschlossen',
                'geplant': 'Geplant',
                'laufend': 'Laufend'
            }
            return names[status] || status
        },
        
        getProjectColor(status) {
            const colors = {
                'abgeschlossen': '#2fe044', // green
                'geplant': '#0076ff',       // blue  
                'laufend': '#ffba00'        // yellow/orange
            }
            return colors[status] || '#707070'
        },
        
        focusOnProject() {
            if (this.focusedProject && this.map && this.map._container && !this.map._animatingZoom) {
                try {
                    // Check if the focused project has valid coordinates
                    const lat = parseFloat(this.focusedProject.lat)
                    const lng = parseFloat(this.focusedProject.lng)
                    
                    if (!isNaN(lat) && !isNaN(lng) && this.focusedProject.lat !== "" && this.focusedProject.lng !== "") {
                        // Center map on the focused project without animation
                        const coordinates = [lat, lng]
                        this.map.setView(coordinates, this.staticZoom, { animate: false })
                        
                        // Find and open the popup for this project
                        const marker = this.markers.find(m => m.projectData.name === this.focusedProject.name)
                        if (marker && this.map.hasLayer(marker)) {
                            marker.openPopup()
                        }
                    }
                } catch (error) {
                    console.warn('Error focusing on project:', error)
                }
            }
        }
    },
    
    beforeUnmount() {
        if (this.map) {
            try {
                // Stop all animations immediately
                if (this.map._animatingZoom) {
                    this.map._animatingZoom = false
                }
                if (this.map._zoom !== undefined) {
                    this.map._zoom = this.map.getZoom()
                }
                
                // Cancel any pending animation frames
                if (this.map._fadeAnimated) {
                    this.map._fadeAnimated = false
                }
                if (this.map._zoomAnimated) {
                    this.map._zoomAnimated = false
                }
                
                // Stop any ongoing animations
                if (this.map.stop) {
                    this.map.stop()
                }
                
                // Close all popups first
                this.map.closePopup()
                
                // Clear all markers with comprehensive cleanup
                this.markers.forEach(marker => {
                    try {
                        if (marker) {
                            // Remove popup event listeners
                            marker.off()
                            // Close any open popups
                            if (marker.isPopupOpen && marker.isPopupOpen()) {
                                marker.closePopup()
                            }
                            // Remove from map if it exists
                            if (this.map && this.map.hasLayer && this.map.hasLayer(marker)) {
                                this.map.removeLayer(marker)
                            }
                        }
                    } catch (e) {
                        // Ignore individual marker errors
                        console.warn('Error removing marker:', e)
                    }
                })
                this.markers = []
                
                // Remove all event listeners from map
                this.map.off()
                
                // Clear any remaining layers
                this.map.eachLayer(layer => {
                    try {
                        this.map.removeLayer(layer)
                    } catch (e) {
                        // Ignore layer removal errors
                    }
                })
                
                // Remove the map instance
                this.map.remove()
            } catch (error) {
                console.warn('Error during map cleanup:', error)
            } finally {
                // Always nullify the map reference
                this.map = null
            }
        }
    }
}
</script>